import { extend } from "lodash";
import { IPromise, IAugmentedJQuery, IAttributes, IScope } from "angular";
import { IPager } from "ng-league";

export interface IPagination {
    getTotalPages(): number;
    getCurrentPage(): number;
    getNextPage(): number;
    getPreviousPage(): number;
    setPage(page: number): void;
    setNextPage(): void;
    setPreviousPage(): void;
}

export interface IHasPagination {
    getPaginationData(offset: number, limit: number): IPromise<any>;
    getContestantPaginationData(contestantId: number, limit: number): IPromise<any>;
}

export interface PaginationObject {
    firstLoad?: boolean;
    loading?: boolean;
    limit?: number;
    total?: number;
    offset?: number;
    data?: any[];
    pages?: any[];
    selected?: any;
    contestant?: any;
}

export interface PaginationScope extends IScope, IPagination, IHasPagination {
    pagination: PaginationObject;
    getPaginationData(offset: number, limit: number): IPromise<any>;
}

export class PaginationDirective implements IPagination {
    public restrict = "AE";
    public link: (scope: IScope, element: IAugmentedJQuery, attrs: IAttributes) => void;
    public scope = false;
    public $scope: PaginationScope = null;
    public transclude = true;
    public templateUrl = "ranking-pagination/pagination.html";

    constructor(private $location) {
        PaginationDirective.prototype.link = (scope: PaginationScope, element: IAugmentedJQuery, attrs: IAttributes) => {
            this.$scope = scope;

            this.$scope.pagination = extend(
                {
                    firstLoad: true,
                    loading: true,
                    total: null,
                    offset: 0,
                    data: [],
                    limit: 100,
                    pages: [],
                    selected: null,
                    contestant: null,
                },
                this.$scope.pagination
            );

            this.onStateChange();
            this.bindMethods();

            let that = this;
            this.$scope.$on("$locationChangeSuccess", function() {
                that.onStateChange();
            });
        };
    }

    private bindMethods() {
        let that = this;

        this.$scope.getTotalPages = () => {
            return that.getTotalPages();
        };

        this.$scope.getCurrentPage = () => {
            return that.getCurrentPage();
        };

        this.$scope.getNextPage = () => {
            return that.getNextPage();
        };

        this.$scope.getPreviousPage = () => {
            return that.getPreviousPage();
        };

        this.$scope.setPage = (number: number) => {
            return that.setPage(number);
        };

        this.$scope.setNextPage = () => {
            return that.setNextPage();
        };

        this.$scope.setPreviousPage = () => {
            return that.setPreviousPage();
        };
    }

    private onLoadStart() {
        this.$scope.pagination.loading = true;
        this.$scope.pagination.data = [];
    }

    private onStateChange() {
        if (typeof this.$location.search().id !== "undefined" && parseInt(this.$location.search().id)) {
            let that = this;
            let id = parseInt(this.$location.search().id);

            this.onLoadStart();
            this.$scope
                .getContestantPaginationData(id, this.$scope.pagination.limit)
                .then((result: any) => {
                    let pager: IPager = result.pager;
                    let data: any[] = result.data;

                    if (data === null || data.length === 0) {
                        that.setPage(1);
                        return;
                    }

                    that.$scope.pagination.loading = false;
                    that.$scope.pagination.data = data;

                    that.$scope.pagination.offset = pager.getOffset();
                    that.onOffsetChange();

                    if (that.$scope.pagination.total !== pager.getTotal()) {
                        that.$scope.pagination.total = pager.getTotal();
                        that.onTotalChange();
                    }
                })
                .catch(function() {
                    that.setPage(1);
                });
        } else if (typeof this.$location.search().page !== "undefined" && parseInt(this.$location.search().page)) {
            let page = parseInt(this.$location.search().page);
            this.setPage(page);
        } else {
            this.setPage(1);
        }
    }

    private onTotalChange() {
        if (this.$scope.pagination.total === null) {
            return;
        }

        this.$scope.pagination.firstLoad = false;

        let pages = [];
        for (let i = 0; i < this.getTotalPages(); i++) {
            let min = this.$scope.pagination.limit * i + 1;
            let max = this.$scope.pagination.limit * i + this.$scope.pagination.limit;

            if (this.$scope.pagination.total < max) {
                max = this.$scope.pagination.total;
            }

            pages.push({
                page: i + 1,
                value: min + ". - " + max + ".",
            });
        }

        this.$scope.pagination.pages = pages;
        this.$scope.pagination.selected = pages[this.getCurrentPage() - 1];
    }

    private onOffsetChange() {
        this.$scope.pagination.selected = this.$scope.pagination.pages[this.getCurrentPage() - 1];
    }

    public getTotalPages() {
        if (this.$scope.pagination.total === null) {
            return 1;
        }

        return Math.ceil(this.$scope.pagination.total / this.$scope.pagination.limit);
    }

    public getCurrentPage() {
        if (this.$scope.pagination.offset < this.$scope.pagination.limit) {
            return 1;
        }

        return Math.ceil(this.$scope.pagination.offset / this.$scope.pagination.limit) + 1;
    }

    public getNextPage() {
        if (this.getTotalPages() === this.getCurrentPage()) {
            return null;
        }

        return this.getCurrentPage() + 1;
    }

    public getPreviousPage() {
        if (this.getCurrentPage() === 1) {
            return null;
        }

        return this.getCurrentPage() - 1;
    }

    public setPage(number: number) {
        if (!this.$scope.pagination.firstLoad && number > this.getTotalPages()) {
            number = this.getTotalPages();
        }
        if (number === null) {
            return;
        }
        if (number === 0) {
            number = 1;
        }

        this.$scope.pagination.offset = this.$scope.pagination.limit * (number - 1);
        this.onOffsetChange();

        if (typeof this.$scope.getPaginationData !== "function") {
            console.error('"$scope.getPaginationData(offset, limit) => Promise" is not implemented');
            return;
        }

        if (!this.$location.search().page) {
            this.$location
                .search({
                    id: null,
                    page: this.getCurrentPage(),
                })
                .replace();

            return; // breaks cycle of: setting page url -> url change event fired -> setting page data -> setting page url
        } else if (this.$location.search().page != this.getCurrentPage()) {
            this.$location.search({
                id: null,
                page: this.getCurrentPage(),
            });

            return; // breaks cycle of: setting page url -> url change event fired -> setting page data -> setting page url
        }

        let that = this;
        this.onLoadStart();
        this.$scope.getPaginationData(this.$scope.pagination.offset, this.$scope.pagination.limit).then((result: any) => {
            let pager: IPager = result.pager;
            let data: any[] = result.data;

            that.$scope.pagination.loading = false;
            that.$scope.pagination.data = data;

            // only update total when data is present
            if (
                !(that.$scope.pagination.total === null && that.$scope.pagination.data.length === 0) &&
                that.$scope.pagination.total !== pager.getTotal()
            ) {
                that.$scope.pagination.total = pager.getTotal();
                that.onTotalChange();
            } else {
                this.$scope.pagination.firstLoad = false;
            }
        });
    }

    public setNextPage() {
        this.setPage(this.getNextPage());
    }

    public setPreviousPage() {
        this.setPage(this.getPreviousPage());
    }

    public static instance() {
        let directive = $location => {
            return new PaginationDirective($location);
        };

        directive["$inject"] = ["$location"];

        return directive;
    }
}
